import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchSermonFeedback,
  deleteSermonFeedbackEntry,
  exportSermonFeedbackCsv,
} from '../../api/client';
import formatDate from '../../utils/formatDate';
import './SermonFeedbackList.css';

const ATTENDED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Attended' },
  { value: 'false', label: 'Did not attend' },
];

function ratingBadgeClass(value) {
  if (value == null) return '';
  if (value >= 4) return 'badge-positive';
  if (value >= 3) return 'badge-pending';
  return 'badge-negative';
}

function outcomeBadgeClass(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

function attendedBadge(attended) {
  if (attended === true) return <span className="badge badge-positive">Attended</span>;
  if (attended === false) return <span className="badge badge-negative">Did not attend</span>;
  return <span className="badge badge-neutral">Unknown</span>;
}

export default function SermonFeedbackList() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [attendedFilter, setAttendedFilter] = useState('');
  const limit = 20;

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (attendedFilter) params.attended = attendedFilter;
      const res = await fetchSermonFeedback(params);
      setEntries(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, attendedFilter]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteSermonFeedbackEntry(id);
        setConfirmId(null);
        loadEntries();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleAttendedChange = (e) => {
    setAttendedFilter(e.target.value);
    setSkip(0);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportSermonFeedbackCsv({ attended: attendedFilter });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && entries.length === 0) {
    return <div className="loading">Loading sermon feedback...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load sermon feedback: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Sermon Feedback Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="sermon-feedback-filter">
        <label htmlFor="sermon-feedback-attended-filter">Filter by attendance:</label>
        <select
          id="sermon-feedback-attended-filter"
          value={attendedFilter}
          onChange={handleAttendedChange}
        >
          {ATTENDED_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {entries.length === 0 ? (
        <p>No sermon feedback responses yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Attended</th>
              <th>Rating</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <React.Fragment key={entry._id}>
                <tr onClick={() => toggleExpand(entry._id)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(entry.created_at)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {entry.phone_number || 'N/A'}
                  </td>
                  <td>{attendedBadge(entry.attended)}</td>
                  <td>
                    {entry.sermon_rating != null ? (
                      <span className={`badge ${ratingBadgeClass(entry.sermon_rating)}`}>
                        {entry.sermon_rating} / 5
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span className={`badge ${outcomeBadgeClass(entry.call_outcome)}`}>
                      {(entry.call_outcome || 'unknown').replace('_', ' ')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {confirmId === entry._id ? (
                      <>
                        <button className="btn-danger-sm" onClick={(e) => handleDelete(e, entry._id)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={(e) => handleDelete(e, entry._id)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
                {expandedId === entry._id && (
                  <tr className="sermon-feedback-detail-row">
                    <td colSpan={6}>
                      <div className="sermon-feedback-details">
                        <div className="sermon-feedback-meta">
                          <span className="detail-label">Call ID:</span>{' '}
                          <span className="detail-value">{entry.call_id || 'N/A'}</span>
                        </div>

                        {entry.attended === true && (
                          <>
                            {(entry.takeaways || []).length > 0 && (
                              <div className="sermon-feedback-section">
                                <div className="sermon-feedback-section-label">Takeaways</div>
                                <ul className="sermon-feedback-bullets">
                                  {entry.takeaways.map((t, i) => <li key={i}>{t}</li>)}
                                </ul>
                              </div>
                            )}
                            {(entry.pastoral_questions || []).length > 0 && (
                              <div className="sermon-feedback-section">
                                <div className="sermon-feedback-section-label">Pastoral Questions</div>
                                <ul className="sermon-feedback-bullets">
                                  {entry.pastoral_questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ul>
                              </div>
                            )}
                          </>
                        )}

                        {entry.attended === false && (
                          <>
                            {entry.absence_reason && (
                              <div className="sermon-feedback-section">
                                <div className="sermon-feedback-section-label">Absence Reason</div>
                                <div className="sermon-feedback-text">{entry.absence_reason}</div>
                              </div>
                            )}
                            {entry.return_likelihood && (
                              <div className="sermon-feedback-section">
                                <div className="sermon-feedback-section-label">Return Likelihood</div>
                                <div className="sermon-feedback-text">{entry.return_likelihood}</div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
